import { useEffect, useMemo, useState } from "react";
import { useTonConnectUI, useTonAddress } from "@tonconnect/ui-react";
import { Address, Cell, beginCell, toNano } from "@ton/core";
import { GOVERNANCE_OPS, PROPOSAL_STATUS, buildProposalPayload } from "@tonado/core";
import { GOVERNANCE_ADDRESS } from "../lib/env";
import { getGovernanceProposal, getGovernanceProposalPayload } from "../lib/relayerRpc";

type Mode = "propose" | "vote" | "queue" | "execute" | "info";

const STATUS_NAME: Record<number, string> = {
  [PROPOSAL_STATUS.ACTIVE]: "ACTIVE",
  [PROPOSAL_STATUS.QUEUED]: "QUEUED",
  [PROPOSAL_STATUS.EXECUTED]: "EXECUTED",
  [PROPOSAL_STATUS.REJECTED]: "REJECTED",
  [PROPOSAL_STATUS.NONE]: "NONE",
};

interface ProposalSnapshot {
  status: number;
  forVotes: bigint;
  againstVotes: bigint;
  votingEnd: number;
  timelockEnd: number;
  proposer: string;
  payload?: { dest: string; value: string; bodyBase64: string };
}

export function GovernancePanel() {
  const [tonConnectUI] = useTonConnectUI();
  const sender = useTonAddress();
  const connected = Boolean(sender);

  const [govAddr, setGovAddr] = useState(GOVERNANCE_ADDRESS);
  const [mode, setMode] = useState<Mode>("info");
  const [proposalId, setProposalId] = useState("0");
  const [destAddr, setDestAddr] = useState("");
  const [destValue, setDestValue] = useState("0");
  const [bodyBoc, setBodyBoc] = useState("");
  const [executeAttached, setExecuteAttached] = useState("0.5");
  const [proposal, setProposal] = useState<ProposalSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const govValid = useMemo(() => safeAddress(govAddr), [govAddr]);

  useEffect(() => {
    if (!govValid || mode !== "info") return;
    (async () => {
      try {
        const id = Number(proposalId);
        if (!Number.isInteger(id) || id < 0) return;
        // Relayer-proxied chain reads — the dApp never holds a toncenter
        // API key, so /governance/proposal and /governance/proposal-payload
        // do the contract get-method calls server-side.
        const p = await getGovernanceProposal(govValid.toString(), id);
        let payload;
        if (p.status !== PROPOSAL_STATUS.NONE) {
          payload = await getGovernanceProposalPayload(govValid.toString(), id);
        }
        setProposal({
          status: p.status,
          forVotes: BigInt(p.forVotes),
          againstVotes: BigInt(p.againstVotes),
          votingEnd: p.votingEnd,
          timelockEnd: p.timelockEnd,
          proposer: p.proposer.toString(),
          payload,
        });
      } catch (err) {
        setError(`Read failed: ${(err as Error).message}`);
      }
    })();
  }, [govValid, proposalId, mode]);

  async function send(opts: { body: Cell; value: bigint; label: string }) {
    if (!govValid) throw new Error("Set Governance address");
    if (!connected) throw new Error("Connect wallet first");
    await tonConnectUI.sendTransaction({
      validUntil: Math.floor(Date.now() / 1000) + 360,
      messages: [
        {
          address: govValid.toString(),
          amount: opts.value.toString(),
          payload: opts.body.toBoc().toString("base64"),
        },
      ],
    });
    setInfo(`${opts.label} submitted.`);
  }

  async function handleSubmit(support?: 0 | 1) {
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (mode === "propose") {
        if (!destAddr) throw new Error("Destination required");
        const dest = Address.parse(destAddr);
        const body = bodyBoc.trim() ? Cell.fromBase64(bodyBoc.trim()) : beginCell().endCell();
        const payload = buildProposalPayload({
          dest,
          value: toNano(destValue || "0"),
          body,
        });
        const opBody = beginCell()
          .storeUint(GOVERNANCE_OPS.PROPOSE, 32)
          .storeUint(0n, 64)
          .storeRef(payload)
          .endCell();
        await send({ body: opBody, value: toNano("0.1"), label: "Propose" });
      } else if (mode === "vote") {
        const id = Number(proposalId);
        if (support !== 0 && support !== 1) throw new Error("Support required");
        const body = beginCell()
          .storeUint(GOVERNANCE_OPS.VOTE, 32)
          .storeUint(0n, 64)
          .storeUint(id, 32)
          .storeUint(support, 1)
          .endCell();
        await send({
          body,
          value: toNano("0.15"),
          label: `Vote ${support === 1 ? "for" : "against"} #${id}`,
        });
      } else if (mode === "queue") {
        const id = Number(proposalId);
        const body = beginCell()
          .storeUint(GOVERNANCE_OPS.QUEUE, 32)
          .storeUint(0n, 64)
          .storeUint(id, 32)
          .endCell();
        await send({ body, value: toNano("0.08"), label: `Queue #${id}` });
      } else if (mode === "execute") {
        const id = Number(proposalId);
        const body = beginCell()
          .storeUint(GOVERNANCE_OPS.EXECUTE, 32)
          .storeUint(0n, 64)
          .storeUint(id, 32)
          .endCell();
        await send({ body, value: toNano(executeAttached), label: `Execute #${id}` });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flow flow--gov">
      <h2 className="flow__title">Governance</h2>
      <p className="lede">
        Propose, vote, queue, and execute on-chain governance actions. Vote weight is your veTONADO
        voting power at the moment your tx lands.
      </p>

      <fieldset className="summary summary--inputs">
        <label className="input-row">
          <span>Governance address</span>
          <input
            type="text"
            value={govAddr}
            onChange={(e) => setGovAddr(e.target.value)}
            placeholder="EQ..."
            className="mono mono--break"
          />
        </label>
      </fieldset>

      <div className="tabs tabs--inline" role="tablist">
        {(["info", "propose", "vote", "queue", "execute"] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            className={`tabs__item ${mode === m ? "tabs__item--active" : ""}`}
            onClick={() => setMode(m)}
          >
            <span className="tabs__label">{m.charAt(0).toUpperCase() + m.slice(1)}</span>
          </button>
        ))}
      </div>

      {(mode === "info" || mode === "vote" || mode === "queue" || mode === "execute") && (
        <label className="input-row">
          <span>Proposal ID</span>
          <input
            type="text"
            value={proposalId}
            onChange={(e) => setProposalId(e.target.value)}
            placeholder="0"
          />
        </label>
      )}

      {mode === "propose" && (
        <>
          <label className="input-row">
            <span>Destination</span>
            <input
              type="text"
              value={destAddr}
              onChange={(e) => setDestAddr(e.target.value)}
              placeholder="EQ..."
              className="mono mono--break"
            />
          </label>
          <label className="input-row">
            <span>Attached TON</span>
            <input
              type="text"
              value={destValue}
              onChange={(e) => setDestValue(e.target.value)}
              placeholder="0"
            />
          </label>
          <label className="input-row">
            <span>Outbound body (base64 BoC, optional)</span>
            <input
              type="text"
              value={bodyBoc}
              onChange={(e) => setBodyBoc(e.target.value)}
              placeholder="te6cck..."
              className="mono mono--break"
            />
          </label>
        </>
      )}

      {mode === "execute" && (
        <label className="input-row">
          <span>Execute attached TON</span>
          <input
            type="text"
            value={executeAttached}
            onChange={(e) => setExecuteAttached(e.target.value)}
            placeholder="0.5"
          />
        </label>
      )}

      {mode === "info" && proposal && (
        <div className="summary">
          <SummaryRow
            label="Status"
            value={STATUS_NAME[proposal.status] ?? `s${proposal.status}`}
            accent
          />
          <SummaryRow label="forVotes" value={proposal.forVotes.toString()} mono />
          <SummaryRow label="againstVotes" value={proposal.againstVotes.toString()} mono />
          <SummaryRow
            label="votingEnd"
            value={proposal.votingEnd ? new Date(proposal.votingEnd * 1000).toISOString() : "-"}
          />
          <SummaryRow
            label="timelockEnd"
            value={proposal.timelockEnd ? new Date(proposal.timelockEnd * 1000).toISOString() : "-"}
          />
          <SummaryRow label="proposer" value={proposal.proposer} mono />
          {proposal.payload && (
            <>
              <SummaryRow label="payload.dest" value={proposal.payload.dest} mono />
              <SummaryRow label="payload.value" value={`${proposal.payload.value} nanoTON`} />
              <SummaryRow label="payload.body" value={proposal.payload.bodyBase64} mono />
            </>
          )}
        </div>
      )}

      {mode === "vote" ? (
        <div className="cta-row">
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || !connected}
            onClick={() => handleSubmit(1)}
          >
            Vote FOR
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy || !connected}
            onClick={() => handleSubmit(0)}
          >
            Vote AGAINST
          </button>
        </div>
      ) : mode !== "info" ? (
        <div className="cta-row">
          <button
            type="button"
            className="btn btn--primary btn--lg"
            disabled={busy || !connected || !govValid}
            onClick={() => handleSubmit()}
          >
            {busy
              ? "Submitting…"
              : !connected
                ? "Connect wallet"
                : mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        </div>
      ) : null}

      {error && (
        <p className="error">
          <strong>Error:</strong> {error}
        </p>
      )}
      {info && <p className="success__kicker">{info}</p>}
    </div>
  );
}

function safeAddress(s: string): Address | null {
  try {
    return Address.parse(s);
  } catch {
    return null;
  }
}

function SummaryRow({
  label,
  value,
  accent,
  mono,
}: {
  label: string;
  value: string;
  accent?: boolean;
  mono?: boolean;
}) {
  return (
    <div className={`summary__row ${accent ? "summary__row--accent" : ""}`}>
      <dt>{label}</dt>
      <dd className={mono ? "mono mono--break" : ""}>{value}</dd>
    </div>
  );
}
